import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import type { MfaFactorType } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { Alert, Button, Field } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';
import { ApiError } from '@/services/api-client';

interface ChallengeState {
  mfaToken: string;
  availableFactors: MfaFactorType[];
  rememberMe?: boolean;
}

/**
 * The second step of signing in.
 *
 * Reached only through router state set by the login page — a page reload
 * here loses the `mfaToken` on purpose (it is not persisted anywhere on the
 * client) and sends the visitor back to sign in again, the same way an
 * expired token would.
 */
export function MfaChallengePage() {
  const { completeMfaChallenge } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ChallengeState | null;

  const [factorType, setFactorType] = useState<MfaFactorType | null>(
    state?.availableFactors[0] ?? null,
  );
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  if (!state?.mfaToken) {
    return <Navigate to="/login" replace />;
  }

  const needsSend = factorType === 'EMAIL' || factorType === 'SMS';

  async function sendCode(): Promise<void> {
    if (!state?.mfaToken || (factorType !== 'EMAIL' && factorType !== 'SMS')) return;
    setError(null);
    setIsSending(true);
    try {
      await authApi.sendMfaChallengeCode(state.mfaToken, factorType);
      setCodeSent(true);
    } catch {
      setError(t('errors.INTERNAL_ERROR'));
    } finally {
      setIsSending(false);
    }
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    void submit();
  }

  async function submit(): Promise<void> {
    if (!state?.mfaToken || !factorType) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await completeMfaChallenge(state.mfaToken, factorType, code, state.rememberMe);
      void navigate('/', { replace: true });
    } catch (caught) {
      const errorCode = caught instanceof ApiError ? caught.code : 'INTERNAL_ERROR';
      setError(t(`errors.${errorCode}`, { fallback: t('errors.INTERNAL_ERROR') }));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
          <p className="auth-subtitle">{t('mfa.challenge.title')}</p>
        </header>

        <form onSubmit={handleSubmit} noValidate className="auth-form">
          {error && <Alert tone="danger">{error}</Alert>}

          {state.availableFactors.length > 1 && (
            <div className="field">
              <label className="field-label" htmlFor="factor-type">
                {t('mfa.challenge.chooseFactor')}
              </label>
              <select
                id="factor-type"
                className="field-input"
                value={factorType ?? ''}
                onChange={(event) => {
                  setFactorType(event.target.value as MfaFactorType);
                  setCodeSent(false);
                  setCode('');
                }}
              >
                {state.availableFactors.map((type) => (
                  <option key={type} value={type}>
                    {t(`mfa.factorType.${type}`)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {needsSend && !codeSent && (
            <Button
              type="button"
              variant="secondary"
              loading={isSending}
              onClick={() => void sendCode()}
            >
              {t('mfa.challenge.sendCode')}
            </Button>
          )}

          {(!needsSend || codeSent) && (
            <>
              <Field
                label={t('mfa.challenge.code')}
                name="code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                autoFocus
                autoComplete="one-time-code"
                dir="ltr"
              />

              {needsSend && (
                <Button
                  type="button"
                  variant="ghost"
                  loading={isSending}
                  onClick={() => void sendCode()}
                >
                  {t('mfa.challenge.resendCode')}
                </Button>
              )}

              <Button type="submit" variant="primary" loading={isSubmitting}>
                {t('mfa.challenge.verify')}
              </Button>
            </>
          )}
        </form>

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
